import { differenceInCalendarDays, parseISO } from 'date-fns';
import { IntlShape } from 'react-intl';

import { CREATE_RECORDS, createRecord } from 'components/create-dialog/recordTypes';
import { fieldName } from 'components/drafts/DraftSaveMode';
import { FormattedDate } from 'components/misc/FormattedDate';
import type { DraftType } from 'types/drafts';
import { AutomatedNotification, NotificationCategory } from 'types/notifications';

/**
 * The three draft templates. Each has a title (`notification.draft-<kind>`, the Inbox's) and a text
 * (`notification.draft-<kind>-text`, worded per design: classic says it all in one sentence, the Inbox's body
 * leaves out what its title says). The backend's own title and content are not shown.
 */
export type DraftNotificationKind = 'shared' | 'completed' | 'expiring';

/** Which draft template a notification is, by what its meta carries; none for any other notification. */
export function draftNotificationKind({
    category,
    metaDataJson,
}: AutomatedNotification): DraftNotificationKind | undefined {
    if (category !== NotificationCategory.DRAFTS || !metaDataJson?.draft) {
        return undefined;
    }
    if (metaDataJson.deletesOn) {
        return 'expiring';
    }
    return metaDataJson.right ? 'shared' : 'completed';
}

/** The placeholders of a draft notification's title and text; `draft` is the record's name, which the text links. */
export function draftNotificationValues({ metaDataJson, createdAt }: AutomatedNotification, intl: IntlShape) {
    const { draft, actor, right, missingFields = [], number, lastSavedOn, deletesOn } = metaDataJson;
    const date = (iso?: string) => (iso ? <FormattedDate key={iso} value={parseISO(iso)} /> : undefined);
    return {
        record: draft ? intl.formatMessage({ id: `create-dialog.record.${draft.type}` }) : undefined,
        // A draft saved before it had a name goes by its create title, "New case".
        draft: draft?.name || (draft ? intl.formatMessage({ id: CREATE_RECORDS[draft.type].titleId }) : undefined),
        actor: actor?.name,
        right,
        missingCount: missingFields.length,
        missingFields: missingFields.map(fieldName).join(', '),
        number,
        numbered: !!number,
        lastSavedOn: date(lastSavedOn),
        deletesOn: date(deletesOn),
        // Counted from the notice, not from today: it says what was true when it was sent.
        daysLeft: deletesOn ? differenceInCalendarDays(parseISO(deletesOn), new Date(createdAt)) : undefined,
    };
}

/** A draft's Inbox tile: the record's glyph on its tile, as the create dialog draws it. */
export function draftTile(type: DraftType) {
    const { icon, tileClass } = createRecord(type);
    return { icon, tileClassName: tileClass };
}
